import {
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { EnvironmentService } from '@app/core/service/environment.service';
import { AsyncSubject, Observable } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private version: string;
  private refreshTokenSubject$: AsyncSubject<any> | null = null;

  public constructor(
    private readonly _authenticationService: AuthenticationService,
    private readonly _environmentService: EnvironmentService
  ) {
    this.version = EnvironmentService.version;
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    const isAbsolute = /^https?:\/\//i.test(request.url);
    const serverUrl = this._environmentService.serverUrl.replace(/\/+$/, '');
    const isExternal = isAbsolute && !request.url.startsWith(serverUrl);

    if (isExternal) {
      return next.handle(request);
    }

    return next.handle(this.addAuthenticationToken(request)).pipe(
      catchError((error) => {
        // We don't want to refresh token for some requests like login or refresh token itself
        // So we verify url and we throw an error if it's the case
        if (
          request.url.includes('user/login') ||
          request.url.includes('user/refresh')
        ) {
          // We do another check to see if refresh token failed
          // In this case we want to logout user and to redirect it to login page
          if (request.url.includes('user/refresh')) {
            this._authenticationService.logout();
          }
          throw error;
        }

        // If error status is different than 401 we want to skip refresh token
        // So we check that and throw the error if it's the case
        if (
          error.status !== 401 &&
          !(error.status === 500 && error.error.includes('jwt expired'))
        ) {
          throw error;
        }

        if (!this.refreshTokenSubject$) {
          this.refreshTokenSubject$ = new AsyncSubject();
          this._authenticationService
            .refreshToken()
            .pipe(
              catchError((e: any) => {
                this._authenticationService.logout();
                throw e;
              }),
              finalize(() => {
                this.refreshTokenSubject$ = null;
              })
            )
            .subscribe(this.refreshTokenSubject$);
        }
        // If refreshTokenInProgress is true, we will wait until refreshTokenSubject has a non-null value
        // – which means the new token is ready and we can retry the request again
        return this.refreshTokenSubject$.pipe(
          switchMap(() => next.handle(this.addAuthenticationToken(request)))
        );
      })
    );
  }

  addAuthenticationToken(request: HttpRequest<any>) {
    const isAbsolute = /^https?:\/\//i.test(request.url);
    const serverUrl = this._environmentService.serverUrl.replace(/\/+$/, '');
    const isExternal = isAbsolute && !request.url.startsWith(serverUrl);

    if (isExternal) {
      return request;
    }

    // Get access token from Local Storage
    const authToken = this._authenticationService.getAuthToken();

    // If access token is null this means that user is not logged in
    // And we return the original request
    if (!authToken) {
      return request;
    }

    let params = request.params.set('AppVersion', this.version);

    if (this._authenticationService.user) {
      params = params.set('AppUser', this._authenticationService.user?._id);
    }

    // We clone the request, because the original request is immutable
    return request.clone({
      headers: request.headers.set(
        'Authorization',
        `Bearer ${authToken?.token || ''}`
      ),
      params
    });
  }
}
